import { useCallback, useEffect, useState } from 'react'
import ComboAsync from 'app/components/ui/combo/combo-async'
import NameIcon from '../shared/NameIcon'
import { StaffForm } from 'app/components/staff/StaffForm'
import type { StaffJson } from 'app/types/entities'
import type { UserJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { staffMembersFetcher } from 'app/lib/fetchers/staffMembersFetcher'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { graphql } from 'app/generated/gql'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { logError, logWarn } from 'lib/logging'
import { lastNameInitial } from 'lib/nameUtils'
import { OptionType } from 'types/OptionType'

// Staff or system users should use black and gray colors on their icons
const borderClassName = `border border-strong`
const backgroundClassName = `bg-surface-strong`
const textClassName = `text-strong font-bold`

const UPDATE_STAFF_MUTATION = graphql(`
  mutation AssignCellUpdateStaff($id: ID!, $input: UpdateStaffInput!) {
    updateStaff(id: $id, input: $input) {
      id
      name
      userId
    }
  }
`)

type Props = {
  user: UserJson
  workspaceId: string
}

export function StaffAssignCellForUser({ user, workspaceId }: Props) {
  const [linkedStaff, setLinkedStaff] = useState<OptionType | undefined>()

  useEffect(() => {
    bg(async () => {
      if (user.id) {
        const result = await gqlFetchList<StaffJson>(
          'staffMembers',
          { search: `userId:${user.id}`, pagination: { limit: 1 } },
          'id name userId',
        )
        if (result.hits.length > 0) {
          setLinkedStaff({ id: result.hits[0].id!, name: result.hits[0].name!, value: result.hits[0] })
        } else {
          logWarn(`No staff found for user ${user.id}`)
          setLinkedStaff(undefined)
        }
      }
    })
  }, [user.id])

  const getNameInitials = () => {
    if (!linkedStaff?.name) return ``
    return `${linkedStaff.name[0]}${lastNameInitial(linkedStaff.name ?? ` `)?.toLocaleUpperCase() ?? ``}`
  }

  const updateStaffAssignment = async (userId: string, staff?: StaffJson) => {
    // Store current state for potential rollback
    const previousLinkedStaff = linkedStaff

    try {
      // Optimistic update - update UI immediately
      if (staff?.id) {
        setLinkedStaff({ id: staff.id!, name: staff.name!, value: staff })
      } else {
        setLinkedStaff(undefined)
      }

      // First, clear any existing staff assignment for this user
      if (previousLinkedStaff?.id) {
        await gqlMutation(UPDATE_STAFF_MUTATION, { id: previousLinkedStaff.id, input: { userId: null } })
      }

      if (staff?.id) {
        // Assign new staff to user by updating the staff's userId field
        await gqlMutation(UPDATE_STAFF_MUTATION, { id: staff.id, input: { userId } })
        notifySuccess(`Staff assigned successfully`)
      } else {
        // Just clearing assignment (no new staff to assign)
        notifySuccess(`Staff assignment removed`)
      }
    } catch (error) {
      // Revert optimistic update on error
      setLinkedStaff(previousLinkedStaff)
      logError(`Error updating staff assignment:`, error)
      notifyError(`Failed to update staff assignment`)
    }
  }

  const openStaffModal = () => {
    showModal(
      <StaffForm
        onSubmit={async (data) => {
          if (data.staff) {
            setLinkedStaff({ id: data.staff.id!, name: data.staff.name!, value: data.staff })
          }
        }}
      />,
      {
        title: `Add Staff`,
        isFullScreen: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  const handleChange = async (val?: OptionType) => {
    if (!val || val.id === `-1`) {
      if (val?.id === `-1`) {
        openStaffModal()
        return
      }

      await updateStaffAssignment(user.id!, undefined)
    } else if (linkedStaff?.id === val.value?.id) {
      // Toggle off if clicking same staff
      await updateStaffAssignment(user.id!, undefined)
    } else {
      // Select new staff
      const staffData = val.value as StaffJson
      await updateStaffAssignment(user.id!, staffData)
    }
  }

  const getOptions = useCallback(() => {
    return (res) => {
      if (res?.total > 0) {
        let options =
          res?.hits?.map((hit: StaffJson) => ({
            id: hit.id!,
            name: hit.name,
            value: hit,
            meta: {
              nameInitial: true,
              borderClassName,
              backgroundClassName,
              textClassName: textClassName,
            },
          })) || []
        // If current user has a linked staff that's not in the options, add it
        // This ensures the currently assigned staff is always visible for deselection
        if (linkedStaff?.value && !options.find((opt) => opt.id === linkedStaff.id)) {
          options.unshift({
            id: linkedStaff.value.id!,
            name: linkedStaff.value.name!,
            value: linkedStaff.value,
            meta: {
              nameInitial: true,
              borderClassName,
              backgroundClassName,
              textClassName: textClassName,
            },
          })
        }

        return options
      }
    }
  }, [linkedStaff])

  return (
    <span className="w-full">
      <ComboAsync
        key={linkedStaff?.id}
        className="px-2"
        placeholder=""
        inputClassName="!h-full !border-0 !shadow-none !py-0 !pl-0 !pr-5 cursor-pointer"
        chevronClassName="hidden group-hover/cell:block"
        showSearchInDropdown={true}
        customIcon={
          linkedStaff?.name && (
            <NameIcon
              fullName={getNameInitials()}
              heightClassName={`h-5`}
              widthClassName={`w-5`}
              borderClassName={borderClassName}
              backgroundClassName={backgroundClassName}
              textClassName={textClassName}
              isTiny
            />
          )
        }
        value={linkedStaff}
        optionsMapper={getOptions()}
        fetcher={staffMembersFetcher}
        onChange={handleChange}
        addText={`Add New Staff Member`}
        onAddNewValue={openStaffModal}
        allowDeselectOption
      />
    </span>
  )
}
